import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, PackageCheckIcon, Undo2Icon } from 'lucide-react';
import { builderPrices, completeSeparateValue, completeUnlocks } from '../../data/builder';
import { CompleteHint } from '../../utils/builderCart';
import { money } from '../QuantityStepper';
interface CompleteUpgradeNoticeProps {
  hint: CompleteHint;
  complete: boolean;
  onUpgrade: () => void;
  onRevert: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;

/** Every Complete System item with its builder price, the total value and what they save. */
function ValueBreakdown() {
  const save = Math.round((completeSeparateValue - builderPrices.complete) * 100) / 100;
  return <div className="mt-3" data-id="element-323" data-mp="11mwgixi7wkmb">
      <ul className="divide-y divide-white/[0.06] border-y border-white/10" data-id="element-324" data-mp="11k0kxftue811">
        {completeUnlocks.map(item => <li key={item.label} className="flex items-center justify-between gap-3 py-1.5 text-[13px]" data-id="element-325" data-mp="2d1a0vsdrihxl">
            <span className="flex items-start gap-2 text-steel-200" data-id="element-326" data-mp="xllh6vui9ej3">
              <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-327" data-mp="xjr60991d0s7" />
              {item.label}
            </span>
            <span className={`shrink-0 tabular-nums ${item.value === null ? 'text-[11px] font-bold uppercase tracking-wide text-grit-500' : 'text-steel-300'}`} data-id="element-328" data-mp="nn4ljlkmgbqp">
              {item.value === null ? 'Included' : money(item.value)}
            </span>
          </li>)}
      </ul>
      <dl className="mt-2 grid grid-cols-[1fr_auto] gap-x-4 gap-y-0.5 text-[14px]" data-id="element-329" data-mp="9w977trct2fq">
        <dt className="text-steel-400" data-id="element-330" data-mp="9ug06dctwg77">Value if bought separately</dt>
        <dd className="text-right tabular-nums text-steel-400 line-through" data-id="element-331" data-mp="4xpkvs8ulomu">{money(completeSeparateValue)}</dd>
        <dt className="font-semibold text-offwhite" data-id="element-332" data-mp="3w5ai2815hni6">X1 Complete System</dt>
        <dd className="text-right font-display text-xl font-extrabold tabular-nums text-offwhite" data-id="element-333" data-mp="3s0knqnm54gzd">{money(builderPrices.complete)}</dd>
        <dt className="font-display text-lg font-extrabold uppercase text-grit-500" data-id="element-334" data-mp="3n4vsuxl2fo9e">You save</dt>
        <dd className="text-right font-display text-lg font-extrabold tabular-nums text-grit-500" data-id="element-335" data-mp="3dh9tc1rxsr44">{money(save)}</dd>
      </dl>
    </div>;
}

/** Shows the Complete System only when it is the smarter deal. Never switches on its own. */
export function CompleteUpgradeNotice({
  hint,
  complete,
  onUpgrade,
  onRevert
}: CompleteUpgradeNoticeProps) {
  const [open, setOpen] = React.useState(false);
  return <AnimatePresence mode="wait" initial={false} data-id="element-336" data-mp="2mxdoyrdek9">
      {complete ? <motion.div key="complete" initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 6
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} className="flex flex-wrap items-center justify-between gap-3 border border-grit-500/60 bg-grit-500/10 px-4 py-3" data-id="element-337" data-mp="4yu77su9za7h">
          <p className="flex items-center gap-2 text-[14px] font-semibold text-offwhite" data-id="element-338" data-mp="npldgliyukhn">
            <PackageCheckIcon className="h-5 w-5 shrink-0 text-grit-500" aria-hidden="true" data-id="element-339" data-mp="28g0h2msf6wi9" />
            X1 Complete System · {money(builderPrices.complete)}. All 3 lenses, case, pouch, cloth and 1-year coverage.
          </p>
          <button type="button" onClick={onRevert} className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide text-steel-300 underline-offset-4 hover:text-offwhite hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-340" data-mp="2h8u270ujsq41">
            <Undo2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-341" data-mp="1kjw6ngez4o5t" />
            Back to Essential
          </button>
        </motion.div> : hint.kind === 'better' ? <motion.div key="better" initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 6
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} role="status" className="border-2 border-grit-500 bg-ink-950" data-id="element-342" data-mp="1thyas4u07wxb">
          <p className="bg-grit-500 px-4 py-2 font-display text-lg font-extrabold uppercase tracking-wide text-ink-950" data-id="element-343" data-mp="448prqkhphlw">
            You unlocked a better deal.
          </p>
          <div className="grid gap-4 p-4 sm:grid-cols-[1fr_auto] sm:items-end sm:p-5" data-id="element-344" data-mp="e0lgcfkqnfcf">
            <div data-id="element-345" data-mp="izy1mvvyoecw">
              <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-[14px]" data-id="element-346" data-mp="j0hkqfxcl8si">
                <dt className="text-steel-400" data-id="element-347" data-mp="n2cpjuehm4b1">Your current core build</dt>
                <dd className="text-right text-steel-400 line-through" data-id="element-348" data-mp="s0wapsl9bc2c">{money(hint.core)}</dd>
                <dt className="font-semibold text-offwhite" data-id="element-349" data-mp="ryjk6r1fntdo">X1 Complete System</dt>
                <dd className="text-right font-display text-xl font-extrabold text-offwhite" data-id="element-350" data-mp="wtpfuhkn0mey">{money(builderPrices.complete)}</dd>
              </dl>
              <p className="mt-3 font-display text-2xl font-extrabold uppercase leading-none text-grit-500" data-id="element-351" data-mp="16qbweu5xkrb6">
                Get more. Pay less.
              </p>
              <ValueBreakdown data-id="element-352" data-mp="1ap18mjf8g2t8" />
            </div>
            <button type="button" onClick={onUpgrade} className="inline-flex items-center justify-center gap-2 bg-grit-500 px-5 py-3.5 font-display text-lg font-extrabold uppercase leading-tight tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-353" data-mp="2vahgvr9cwuqn">
              Get Complete · save {money(hint.save)} · Checkout
              <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-354" data-mp="39yugqzbkexqf" />
            </button>
          </div>
        </motion.div> : hint.kind === 'close' || hint.kind === 'near' ? <motion.div key={hint.kind} initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 6
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} className={`border px-4 py-3 ${hint.kind === 'close' ? 'border-grit-500/70 bg-grit-500/10' : 'border-white/12 bg-white/[0.03]'}`} data-id="element-355" data-mp="307hkzwmnbnf4">
          <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-356" data-mp="1v0zvyp0tl9vz">
            <p className={`font-display font-extrabold uppercase leading-tight ${hint.kind === 'close' ? 'text-lg text-grit-500' : 'text-base text-offwhite'}`} data-id="element-357" data-mp="1v32pvk53fn5z">
              {hint.kind === 'close' ? `Only ${money(hint.diff)} more for Complete` : `Only ${money(hint.diff)} more for the Complete System`}
            </p>
            <button type="button" aria-expanded={open} onClick={() => setOpen(value => !value)} className="text-[12px] font-bold uppercase tracking-wide text-grit-500 underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-358" data-mp="1cbjlb8vf811u">
              {open ? 'Hide' : 'See everything you unlock'}
            </button>
          </div>
          <AnimatePresence initial={false} data-id="element-359" data-mp="2xlqqsbir306i">
            {open && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: EASE
        }} className="overflow-hidden" data-id="element-360" data-mp="37sjir1fb3xfg">
                <ValueBreakdown data-id="element-361" data-mp="3bs21bqjnnwf4" />
                <button type="button" onClick={onUpgrade} className="mt-4 inline-flex w-full items-center justify-center gap-2 bg-grit-500 px-4 py-3.5 font-display text-lg font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite sm:w-auto" data-id="element-362" data-mp="3qij53gjriyf6">
                  Get Complete — {money(builderPrices.complete)} · Checkout
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-363" data-mp="2155vbbsvsi6o" />
                </button>
              </motion.div>}
          </AnimatePresence>
        </motion.div> : null}
    </AnimatePresence>;
}